/**
 * @fileoverview USD Liquidity Year-over-Year Signal
 * Computes YoY change in global USD liquidity.
 * @module signals/liquidity/usd_liquidity_yoy
 *
 * CONTRACT:
 * - Input: { series: TimeseriesPoint[] } where values are liquidity measurements
 * - Output: UsdLiquidityYoyOutput with yoy change, direction, current/prior values
 * - Errors: Throws if series is invalid (empty, malformed, or contains NaN)
 * - Behavior: Pure function, deterministic output for same input
 *
 * DOMAIN CONTEXT:
 * Global USD liquidity tracks offshore dollar credit and funding conditions.
 * Expanding liquidity supports risk assets; contracting liquidity creates headwinds.
 * YoY comparison uses ~12 periods lookback (appropriate for monthly data).
 */

import { isValidSeries, createSignalValidationError } from "../types.js";

/**
 * Input for the USD liquidity YoY signal.
 * @typedef {Object} UsdLiquidityYoyInput
 * @property {import('../types.js').TimeseriesPoint[]} series - USD liquidity series (e.g., in trillions)
 */

/**
 * Output from the USD liquidity YoY signal.
 * @typedef {Object} UsdLiquidityYoyOutput
 * @property {number} yoy - Year-over-year percentage change (rounded to 2 decimals)
 * @property {'expanding' | 'contracting' | 'flat'} direction - Liquidity direction
 * @property {number} currentValue - Most recent liquidity value (rounded to 2 decimals)
 * @property {number} priorYearValue - Value from ~12 periods ago (rounded to 2 decimals)
 */

/**
 * Threshold for determining flat direction (in percentage points).
 * @type {number}
 */
const FLAT_THRESHOLD = 1.0;

/**
 * Number of periods to look back for YoY comparison.
 * @type {number}
 */
const LOOKBACK_PERIODS = 12;

/**
 * Determines liquidity direction from YoY change.
 * @param {number} yoy - Year-over-year percentage change
 * @returns {'expanding' | 'contracting' | 'flat'} Direction
 */
function determineDirection(yoy) {
  if (yoy > FLAT_THRESHOLD) return "expanding";
  if (yoy < -FLAT_THRESHOLD) return "contracting";
  return "flat";
}

/**
 * USD Liquidity YoY Signal Definition.
 *
 * Measures the year-over-year change in global USD liquidity by comparing
 * the most recent value to the value ~12 periods earlier.
 *
 * @type {import('../types.js').SignalDefinition<UsdLiquidityYoyInput, UsdLiquidityYoyOutput>}
 */
export const usdLiquidityYoySignal = {
  id: "usd_liquidity_yoy",
  name: "USD Liquidity YoY",
  description:
    "Measures the year-over-year change in global USD liquidity. " +
    "Expanding liquidity supports risk assets; contracting liquidity creates headwinds. " +
    "Uses ~12 periods lookback for YoY comparison.",
  category: "liquidity",

  /**
   * Computes YoY liquidity change from time series.
   *
   * @param {UsdLiquidityYoyInput} input - Input containing liquidity series
   * @returns {UsdLiquidityYoyOutput} Computed YoY output
   * @throws {Error} If series is invalid (empty, malformed, or contains NaN values)
   *
   * @example
   * const result = usdLiquidityYoySignal.compute({
   *   series: [
   *     { t: "2024-01-01", v: 10 },
   *     // ... 11 more periods ...
   *     { t: "2025-01-01", v: 11 }
   *   ]
   * });
   * // result: { yoy: 10, direction: "expanding", currentValue: 11, priorYearValue: 10 }
   */
  compute(input) {
    const { series } = input;

    if (!isValidSeries(series)) {
      throw new Error(
        createSignalValidationError(
          "usd_liquidity_yoy",
          "Series must be a non-empty array of valid { t, v } points with no NaN values"
        )
      );
    }

    const currentValue = series[series.length - 1].v;

    // Look back ~12 periods (approximating 1 year for monthly data)
    // For shorter series, use what's available
    const lookbackPeriods = Math.min(LOOKBACK_PERIODS, series.length - 1);
    const priorIndex = series.length - 1 - lookbackPeriods;
    const priorYearValue = series[Math.max(0, priorIndex)].v;

    // Handle edge case: prior value is zero (would cause division by zero)
    if (priorYearValue === 0) {
      throw new Error(
        createSignalValidationError(
          "usd_liquidity_yoy",
          "Prior year value cannot be zero (division by zero)"
        )
      );
    }

    const yoy = ((currentValue - priorYearValue) / priorYearValue) * 100;
    const direction = determineDirection(yoy);

    return {
      yoy: Math.round(yoy * 100) / 100,
      direction,
      currentValue: Math.round(currentValue * 100) / 100,
      priorYearValue: Math.round(priorYearValue * 100) / 100,
    };
  },
};
